import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ChevronDown, ChevronRight, HelpCircle, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SequenceFormData } from '../types';
import PropsMultiSelect from '../PropsMultiSelect';

interface PropsSectionProps {
  form: UseFormReturn<SequenceFormData>;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

const PropsSection: React.FC<PropsSectionProps> = ({
  form,
  isExpanded,
  onToggleExpanded,
}) => {
  // Session storage persistence for section state
  useEffect(() => {
    const savedState = sessionStorage.getItem('props-section-expanded');
    if (savedState === 'true' && !isExpanded) {
      onToggleExpanded();
    }
  }, []);

  const handleToggleExpanded = () => {
    const newState = !isExpanded;
    sessionStorage.setItem('props-section-expanded', newState.toString());
    onToggleExpanded();
  };

  const watchProps = form.watch('props') || [];

  return (
    <Card className="w-full border-2 border-blue-200 bg-blue-50/30">
      <CardHeader
        className={`${isExpanded ? '' : 'cursor-pointer hover:bg-blue-100/50'} transition-colors`}
        onClick={isExpanded ? undefined : handleToggleExpanded}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5" />
            <span>Props & Equipment</span>
            <span className="text-sm font-normal text-gray-500">(Optional)</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Select the physical props (Mega Trees, Snowflakes, Arches, etc.) used in your sequence.</p>
                  <p className="text-xs mt-1 text-blue-200">Helps buyers know what equipment they need.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          {!isExpanded && (
            <div className="flex items-center gap-2">
              {watchProps.length > 0 && (
                <span className="text-sm text-blue-700">
                  {watchProps.length} prop{watchProps.length !== 1 ? 's' : ''} selected
                </span>
              )}
              <ChevronRight className="h-5 w-5 text-gray-500" />
            </div>
          )}
          {isExpanded && (
            <ChevronDown className="h-5 w-5 text-gray-500 cursor-pointer" onClick={handleToggleExpanded} />
          )}
        </div>
        {isExpanded && (
          <p className="text-sm text-blue-700 mt-2">
            Select the props and equipment buyers will need to run your sequence. This helps them find sequences compatible with their display.
          </p>
        )}
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          <PropsMultiSelect form={form} max={20} />

          <div className="p-4 bg-blue-100 rounded-lg">
            <h4 className="font-medium text-sm mb-2 text-blue-900">Why add props?</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Helps buyers find sequences compatible with their equipment</li>
              <li>• Increases visibility in search results</li>
              <li>• Buyers can filter by specific props (Mega Trees, Snowflakes, etc.)</li>
              <li>• Shows professionalism and attention to detail</li>
            </ul>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default PropsSection;
